import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PrimeArtifactStateTransition, PrimeArtifactState } from '../../types/primeModel';
import { selectPrimeArtifactStates } from '../../store/selectors/primeArtifactSelectors';
import { usePrimeArtifact } from '../../store/hooks/usePrimeArtifact';
import styles from './PrimeTransitionManager.module.css';
import { StateTransitionGenerator } from './StateTransitionGenerator';
import { toast } from 'react-hot-toast';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../types/enums';
import JsonFieldEditor from '../common/JsonFieldEditor';
import {
  improvementParametersTemplate,
  validationRulesTemplate,
  transitionParametersTemplate,
} from '../../types/templates/transitionTemplates';
import { apiClient } from '../../services/apiClient';
import { usePrimeModel } from '../../contexts/PrimeModelContext';

interface TransitionManagerProps {
  prime_artifact_id: string;
  onTransitionUpdate: (transition: PrimeArtifactStateTransition) => void;
  _onTransitionCreate: (transition: Omit<PrimeArtifactStateTransition, 'id'>) => void;
}

const TransitionManager: React.FC<TransitionManagerProps> = ({
  prime_artifact_id,
  _onTransitionCreate,
}) => {
  const states = useSelector(selectPrimeArtifactStates);
  const { transitions, fetchTransitions, fetchStates, loading, error } =
    usePrimeArtifact(prime_artifact_id);
  const { selectedArtifact } = usePrimeModel();
  const [selectedTransition, setSelectedTransition] = useState<PrimeArtifactStateTransition | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [autoGenerateProcess, setAutoGenerateProcess] = useState(true);
  const [generatingProcess, setGeneratingProcess] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // First fetch states
        await fetchStates();
        // Then fetch transitions
        await fetchTransitions();
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    void loadData();
  }, [fetchStates, fetchTransitions]);

  const getStateName = (stateId: string) => {
    const state = states.find((s: PrimeArtifactState) => s.id === stateId);
    if (!state) {
      console.warn(`State not found for ID: ${stateId}`);
      return 'Unknown State';
    }
    return state.name;
  };

  const handleTransitionSelect = (transition: PrimeArtifactStateTransition) => {
    setSelectedTransition(transition);
    setIsEditing(false);
  };

  const handleTransitionUpdate = async (updatedTransition: PrimeArtifactStateTransition) => {
    try {
      await _onTransitionCreate(updatedTransition);
      await fetchTransitions();
      
      // Auto-generate process if enabled
      if (autoGenerateProcess && updatedTransition.id) {
        setGeneratingProcess(true);
        try {
          const response = await apiClient.post(
            `/prime-transitions/${updatedTransition.id}/generate-process`,
            {}
          );
          toast.success(response.data.message || 'Transition updated and process generated');
        } catch (processError: any) {
          console.error('Error generating process:', processError);
          toast.error(`Transition updated, but failed to generate process: ${processError.response?.data?.detail || processError.message}`);
        } finally {
          setGeneratingProcess(false);
        }
      } else {
        toast.success('Transition updated successfully');
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating transition:', error);
      toast.error('Failed to update transition');
    }
  };

  const handleDelete = async (_transitionId: string) => {
    try {
      // Delete transition logic here
      setSelectedTransition(null);
      await fetchTransitions();
      toast.success('Transition deleted successfully');
    } catch (error) {
      console.error('Error deleting transition:', error);
      toast.error('Failed to delete transition');
    }
  };

  const handleGeneratedTransitions = async (
    generatedTransitions: PrimeArtifactStateTransition[]
  ) => {
    try {
      for (const transition of generatedTransitions) {
        await _onTransitionCreate(transition);
      }
      await fetchTransitions();
      setIsGenerating(false);
      toast.success('Transitions generated successfully');
    } catch (error) {
      console.error('Error creating transitions:', error);
      toast.error('Failed to create transitions');
    }
  };

  /**
   * Get the suggested process name based on artifact name and transition name
   */
  const getSuggestedProcessName = () => {
    if (!selectedArtifact || !selectedTransition) return '';
    return `${selectedArtifact.name} - ${selectedTransition.name}`;
  };

  /**
   * Get role-specific hints for the selected transition type
   */
  const getRoleHint = (role: ArtifactStateTransitionType): string => {
    switch (role) {
      case ArtifactStateTransitionType.OPERATIONAL:
        return 'Operational: Standard workflow progression between states.';
      case ArtifactStateTransitionType.IMPROVEMENT:
        return 'Improvement: Artifact definition lifecycle (orthogonal to operational workflow).';
      case ArtifactStateTransitionType.MATERIAL:
        return 'Material: Consumed in the process. Placeholder process with type MATERIAL_CONSUMPTION will be created.';
      case ArtifactStateTransitionType.RESOURCE:
        return 'Resource: Used but not consumed. No state change occurs. Process association only.';
      case ArtifactStateTransitionType.CREATION:
        return 'Creation: Artifact is created by the process. Placeholder process will be created, likely owned by a different artifact.';
      default:
        return 'Select a transition type to see role-specific guidance.';
    }
  };

  const renderTransitionList = () => {
    if (loading) {
      return <div className={styles.loading}>Loading transitions...</div>;
    }

    if (error) {
      return <div className={styles.error}>Error loading transitions: {error}</div>;
    }

    if (!transitions?.length) {
      return <div className={styles.noTransitions}>No transitions found</div>;
    }

    // Check if we have all required states
    const missingStates = transitions.some(
      (transition) =>
        !states.find((s) => s.id === transition.from_state_ref) ||
        !states.find((s) => s.id === transition.to_state_ref)
    );

    if (missingStates) {
      return <div className={styles.error}>Some states are missing. Please refresh the page.</div>;
    }

    return (
      <div className={styles.transitionList}>
        {transitions.map((transition: PrimeArtifactStateTransition) => (
          <div
            key={transition.id}
            className={`${styles.transitionItem} ${selectedTransition?.id === transition.id ? styles.selected : ''}`}
            onClick={() => handleTransitionSelect(transition)}
          >
            <span className={styles.transitionArrow}>
              {getStateName(transition.from_state_ref)} → {getStateName(transition.to_state_ref)}
            </span>
            <span className={styles.transitionType} title={transition.default_transition_type}>
              {transition.default_transition_type?.slice(0, 3).toUpperCase()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderTransitionDetails = () => {
    if (!selectedTransition) {
      return <div className={styles.noSelection}>Select a transition to view details</div>;
    }

    // Check if we have the required states
    const fromState = states.find((s) => s.id === selectedTransition.from_state_ref);
    const toState = states.find((s) => s.id === selectedTransition.to_state_ref);

    if (!fromState || !toState) {
      return <div className={styles.error}>Required states are missing. Please refresh the page.</div>;
    }

    if (isEditing) {
      return (
        <div className={styles.transitionEditPanel}>
          <h3>Edit Transition</h3>
          <div className={styles.editForm}>
            <div className={styles.formGroup}>
              <label>Name</label>
              <input
                type="text"
                value={selectedTransition.name}
                onChange={(e) =>
                  setSelectedTransition({ ...selectedTransition, name: e.target.value })
                }
              />
            </div>
            <div className={styles.formGroup}>
              <label>Description</label>
              <textarea
                value={selectedTransition.description || ''}
                onChange={(e) =>
                  setSelectedTransition({ ...selectedTransition, description: e.target.value })
                }
              />
            </div>
            <div className={styles.formGroup}>
              <label>Type</label>
              <select
                value={selectedTransition.default_transition_type}
                onChange={(e) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    default_transition_type: e.target.value as ArtifactStateTransitionType,
                  })
                }
              >
                <option value={ArtifactStateTransitionType.OPERATIONAL}>Operational</option>
                <option value={ArtifactStateTransitionType.MATERIAL}>Material</option>
                <option value={ArtifactStateTransitionType.RESOURCE}>Resource</option>
                <option value={ArtifactStateTransitionType.CREATION}>Creation</option>
                <option value={ArtifactStateTransitionType.IMPROVEMENT}>Improvement</option>
              </select>
              <div className={styles.roleHint} style={{ marginTop: '8px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                {getRoleHint(selectedTransition.default_transition_type)}
              </div>
            </div>
            <div className={styles.formGroup} style={{ marginTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoGenerateProcess}
                  onChange={(e) => setAutoGenerateProcess(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span>Auto-generate process on save</span>
              </label>
              {autoGenerateProcess && (
                <div className="process-preview" style={{ 
                  marginTop: '12px', 
                  padding: '12px', 
                  background: '#f0f7ff', 
                  border: '1px solid #91caff',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '13px', color: '#0958d9', marginBottom: '4px', fontWeight: 500 }}>
                    Process will be named:
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#003eb3' }}>
                    {getSuggestedProcessName()}
                  </div>
                </div>
              )}
              {generatingProcess && (
                <div style={{ marginTop: '8px', color: '#1890ff', fontSize: '13px' }}>
                  ⏳ Generating process...
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label>Demand Type</label>
              <select
                value={selectedTransition.demand_type}
                onChange={(e) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    demand_type: e.target.value as DemandType,
                  })
                }
              >
                <option value={DemandType.TARGET}>Target</option>
                <option value={DemandType.CAPACITY}>Capacity</option>
                <option value={DemandType.SUPPLY}>Supply</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Demand Flow Type</label>
              <select
                value={selectedTransition.demand_flow_type}
                onChange={(e) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    demand_flow_type: e.target.value as DemandFlowType,
                  })
                }
              >
                <option value={DemandFlowType.DRUM}>Drum</option>
                <option value={DemandFlowType.PULL}>Pull</option>
                <option value={DemandFlowType.PUSH}>Push</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <JsonFieldEditor
                label="Transition Parameters"
                description="Configure the parameters for this transition"
                value={selectedTransition.transition_parameters || {}}
                onChange={(value) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    transition_parameters: value as Record<string, unknown>,
                  })
                }
                template={transitionParametersTemplate}
              />
            </div>
            <div className={styles.formGroup}>
              <JsonFieldEditor
                label="Validation Rules"
                description="Configure the validation rules for this transition"
                value={selectedTransition.validation_rules || {}}
                onChange={(value) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    validation_rules: value as Record<string, unknown>,
                  })
                }
                template={validationRulesTemplate}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Improvement Reference</label>
              <input
                type="text"
                value={selectedTransition.improvement_ref || ''}
                onChange={(e) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    improvement_ref: e.target.value || null,
                  })
                }
                placeholder="Enter improvement reference ID"
              />
            </div>
            <div className={styles.formGroup}>
              <JsonFieldEditor
                label="Improvement Parameters"
                description="Configure the improvement parameters for this transition"
                value={selectedTransition.improvement_parameters || {}}
                onChange={(value) =>
                  setSelectedTransition({
                    ...selectedTransition,
                    improvement_parameters: value as Record<string, unknown>,
                  })
                }
                template={improvementParametersTemplate}
              />
            </div>
            <div className={styles.editActions}>
              <button onClick={() => handleTransitionUpdate(selectedTransition)}>Save</button>
              <button onClick={() => setIsEditing(false)}>Cancel</button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.transitionDetails}>
        <div className={styles.detailsHeader}>
          <h3>{selectedTransition.name}</h3>
          <div className={styles.detailsActions}>
            <button onClick={() => setIsEditing(true)}>Edit</button>
            <button onClick={() => handleDelete(selectedTransition.id)}>Delete</button>
          </div>
        </div>
        <div className={styles.detailsContent}>
          <p className={styles.description}>{selectedTransition.description}</p>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>From State</label>
              <span>{fromState.name}</span>
            </div>
            <div className={styles.detailItem}>
              <label>To State</label>
              <span>{toState.name}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Type</label>
              <span>{selectedTransition.default_transition_type}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Demand Type</label>
              <span>{selectedTransition.demand_type}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Demand Flow Type</label>
              <span>{selectedTransition.demand_flow_type}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.transitionManager}>
      <div className={styles.header}>
        <h2>Transitions</h2>
        <div className={styles.headerActions}>
          <button onClick={() => setIsGenerating(true)} className={styles.generateButton}>
            Generate Transitions
          </button>
        </div>
      </div>

      {isGenerating && (
        <StateTransitionGenerator
          states={states}
          artifactId={prime_artifact_id}
          onGenerate={handleGeneratedTransitions}
          onCancel={() => setIsGenerating(false)}
        />
      )}

      <div className={styles.transitionContent}>
        <div className={styles.transitionListContainer}>{renderTransitionList()}</div>
        <div className={styles.transitionDetailsContainer}>{renderTransitionDetails()}</div>
      </div>
    </div>
  );
};

export default TransitionManager;
